import { useTranslation } from "react-i18next";
import { FC, useMemo } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { replaceDate } from "@/helper/dateHandling";
import classNames from "classnames";
import styles from "@/components/subjectOverview/subjectOverviewMaillog.module.css";

interface SubjectOverviewMaillogProps {
  detailedSubject: any;
}

export const SubjectOverviewMailLog: FC<SubjectOverviewMaillogProps> = ({
  detailedSubject,
}) => {
  const { t } = useTranslation();

  const statusFilterOptions = useMemo(() => {
    return [
      { label: t("mail_status_in_progress"), value: "in_progress" },
      { label: t("mail_status_submitted"), value: "submitted" },
      { label: t("mail_status_suppressed"), value: "suppressed" },
      { label: t("mail_status_delivered"), value: "delivered" },
      { label: t("mail_status_failed-temporary"), value: "failed-temporary" },
      { label: t("mail_status_failed-permanent"), value: "failed-permanent" },
      { label: t("mail_status_failed-mailgun"), value: "failed-mailgun" },
      { label: t("mail_status_complained"), value: "complained" },
      { label: t("mail_status_committed"), value: "committed" },
    ];
  }, [t]);

  const columns = useMemo<CrudColumnDef[]>(
    () => [
      {
        accessorKey: "id",
        header: t("id"),
        id: "id",
      },
      {
        accessorKey: "sent_on",
        header: t("maillog_sent_on"),
        id: "sent_on",
        filterVariant: "date",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true, true)}</span>;
        },
      },
      {
        accessorKey: "created_on",
        header: t("created_on"),
        id: "created_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue(), true, true)}</span>;
        },
      },
      {
        accessorKey: "status",
        header: t("status"),
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        accessorFn: (row: any) => {
          const status = row.status;
          return (
            <span
              className={classNames(styles.span_status_default, {
                [styles.span_status_delivered]: status === "delivered",
                [styles.span_status_error]: status === "failed-permanent",
              })}
            >
              {t(`mail_status_${status}`)}
            </span>
          );
        },
      },
      {
        accessorKey: "template.name",
        header: t("template"),
        id: "name",
        customColumnFilterId: "template__name",
        customSortingKey: "name",
      },
      {
        accessorKey: "template.mail_subject",
        header: t("mail_subject"),
        id: "mail_subject",
        customColumnFilterId: "template__mail_subject",
        customSortingKey: "mail_subject",
      },
      {
        accessorKey: "metadata.entity_id",
        header: t("entity_id"),
        id: "entity_id",
        customColumnFilterId: "metadata__entity_id",
        customSortingKey: "entity_id",
      },
      {
        accessorKey: "metadata.entity_type",
        header: t("entity_type"),
        id: "entity_type",
        customColumnFilterId: "metadata__entity_type",
        customSortingKey: "entity_type",
      },
      {
        accessorKey: "metadata.suppression.source_subject_id",
        header: t("mail_preferences_suppression_source"),
        id: "suppression_source",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "modified_on",
        header: t("modified_on"),
        id: "modified_on",
        filterVariant: "date",
        sortingFn: "datetime",
        Cell: ({ cell }: any) => {
          return <span>{replaceDate(cell.getValue())}</span>;
        },
      },
      ...getRecordMetadataColumns(t, ["created_by", "modified_by"]),
    ],
    []
  );

  return (
    <>
      <GenericCrudTable
        entity="mailLog"
        columns={columns}
        customGlobalFilterPosition={"left"}
        customDensity="compact"
        customPageSize={5}
        showRowActions={false}
        searchId={detailedSubject?.id}
        expandedDepth={true}
        tableCollapseWidth={"90%"}
        readOnlyMode
        customColumnVisibility={{
          entity_type: false,
          entity_id: false,
          created_on: false,
        }}
      />
    </>
  );
};

export default SubjectOverviewMailLog;
